import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";

import { STRING_NOT_NULL_HELPER } from "../../common/helpersTxt";
import { searchCep } from "../../extras";

import AddressService from "../../api/address";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapIcon from "@mui/icons-material/Map";
import NumbersIcon from "@mui/icons-material/Numbers";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Animation variants
const modalVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -50,
    scale: 0.95,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function CreateOrEditAddress({
  show,
  closeModal,
  updateInfos,
  address,
  customer_id = null,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);

  // Form fields
  const [addressId, setAddressId] = useState(address?.address_id || uuidv4());
  const [street, setStreet] = useState(address?.street || "");
  const [isStreetValid, setIsStreetValid] = useState(
    !!address?.street || false
  );
  const [houseNumber, setHouseNumber] = useState(address?.house_number || "");
  const [isHouseNumberValid, setIsHouseNumberValid] = useState(
    !!address?.house_number || false
  );
  const [neighborhood, setNeighborhood] = useState(address?.neighborhood || "");
  const [isNeighborhoodValid, setIsNeighborhoodValid] = useState(
    !!address?.neighborhood || false
  );
  const [complement, setComplement] = useState(
    address?.address_complement || ""
  );
  const [reference, setReference] = useState(address?.address_reference || "");
  const [cep, setCep] = useState(address?.cep || "");
  const [isCEPValid, setIsCEPValid] = useState(!!address?.cep || false);
  const [city, setCity] = useState(address?.city || "");
  const [isCityValid, setIsCityValid] = useState(!!address?.city || false);
  const [uf, setUf] = useState(address?.uf || "");
  const [isUfValid, setIsUfValid] = useState(!!address?.uf || false);

  // Error handling
  const [error, setError] = useState("");

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const loadData = async () => {
    if (address) {
      setIsEditing(true);
    }
    setLoading(false);
  };

  const setDataFromCep = async () => {
    if (cep && cep.length === 8) {
      try {
        setSearchingCep(true);
        setError("");
        const data = await searchCep(cep);

        if (!data || data.erro) {
          setError("CEP não encontrado. Verifique o número informado.");
          return;
        }

        setStreet(data.logradouro || "");
        setIsStreetValid(!!data.logradouro);
        setNeighborhood(data.bairro || "");
        setIsNeighborhoodValid(!!data.bairro);
        setCity(data.localidade || "");
        setIsCityValid(!!data.localidade);
        setUf(data.uf || "");
        setIsUfValid(!!data.uf);
      } catch (err) {
        setError(
          "Erro ao buscar CEP. Tente novamente ou preencha manualmente."
        );
      } finally {
        setSearchingCep(false);
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    // Validate all required fields
    const isStreetOk = street.trim().length > 0;
    const isHouseNumberOk = houseNumber.trim().length > 0;
    const isNeighborhoodOk = neighborhood.trim().length > 0;
    const isCityOk = city.trim().length > 0;
    const isUfOk = uf.trim().length > 0;

    setIsStreetValid(isStreetOk);
    setIsHouseNumberValid(isHouseNumberOk);
    setIsNeighborhoodValid(isNeighborhoodOk);
    setIsCityValid(isCityOk);
    setIsUfValid(isUfOk);

    const isValid =
      isStreetOk && isHouseNumberOk && isNeighborhoodOk && isCityOk && isUfOk;

    if (isValid) {
      try {
        setSaving(true);
        setError("");

        // Get coordinates from address
        const address_coord = await AddressService.addressCoordinates(
          street,
          houseNumber,
          neighborhood,
          city,
          cep
        );

        const addressObj = {
          address_id: isEditing ? address.address_id : addressId,
          street: street.trim(),
          neighborhood: neighborhood.trim(),
          house_number: houseNumber.trim(),
          address_complement: complement.trim(),
          address_reference: reference.trim(),
          cep: cep.trim(),
          city: city.trim(),
          uf: uf.trim(),
          lat: parseFloat(address_coord.lat.toFixed(7)),
          long: parseFloat(address_coord.lng.toFixed(7)),
          fk_customer: customer_id,
        };

        try {
          if (isEditing) {
            await AddressService.editAddress(addressObj, addressId);
          } else {
            await AddressService.createAddress(addressObj);
          }

          setSuccess(true);
          setTimeout(() => {
            updateInfos();
            closeModal();
          }, 1000);
        } catch (e) {
          setError(e.response?.data?.error || "Erro ao salvar endereço");
        }
      } catch (e) {
        setError(
          e.response?.data?.error ||
            "Não foi possível obter as coordenadas do endereço. Verifique os dados informados."
        );
      } finally {
        setSaving(false);
      }
    } else {
      setError("Preencha todos os campos obrigatórios antes de continuar");
    }
  };

  return (
    <Modal
      aria-labelledby="address-modal-title"
      aria-describedby="address-modal-description"
      open={show}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Fade in={show}>
        <Box
          component={motion.div}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          sx={{
            position: "relative",
            width: { xs: "95%", sm: "90%", md: "600px" },
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 3,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 0,
            "&:focus-visible": {
              outline: "none",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                p: 8,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={60} />
                <Typography variant="body1" color="textSecondary">
                  Carregando endereço...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 3,
                  borderBottom: `1px solid ${alpha(
                    theme.palette.divider,
                    0.1
                  )}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: 2,
                      p: 1,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <LocationOnIcon />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      id="address-modal-title"
                    >
                      {isEditing ? "Editar Endereço" : "Novo Endereço"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isEditing
                        ? "Modifique as informações do endereço"
                        : "Preencha os dados para cadastrar um novo endereço"}
                    </Typography>
                  </Box>
                </Stack>

                <IconButton
                  aria-label="close"
                  onClick={closeModal}
                  sx={{
                    color: theme.palette.grey[500],
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.grey[500], 0.2),
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                {/* Error message */}
                {error && (
                  <Alert
                    severity="error"
                    variant="filled"
                    icon={<ErrorOutlineIcon />}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setError("")}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Success message */}
                {success && (
                  <Alert
                    severity="success"
                    variant="filled"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    Endereço salvo com sucesso!
                  </Alert>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  {/* CEP Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <MyLocationIcon fontSize="small" color="primary" />
                      Busca por CEP
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <TextField
                        variant="outlined"
                        id="address-cep"
                        name="address-cep"
                        label="CEP"
                        placeholder="Apenas números"
                        required
                        fullWidth
                        error={!isCEPValid && submitted}
                        value={cep}
                        helperText={
                          !isCEPValid && submitted ? STRING_NOT_NULL_HELPER : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value.replace(/\D/g, "");
                          setIsCEPValid(value.length > 0);
                          setCep(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <MapIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        inputProps={{ maxLength: 8 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />

                      <motion.div
                        whileHover={!searchingCep ? "hover" : undefined}
                        whileTap={!searchingCep ? "tap" : undefined}
                        variants={buttonVariants}
                      >
                        <Button
                          variant="contained"
                          onClick={setDataFromCep}
                          disabled={cep.length !== 8 || searchingCep}
                          startIcon={
                            searchingCep ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SearchIcon />
                            )
                          }
                          sx={{
                            borderRadius: 2,
                            height: 56,
                            minWidth: 120,
                          }}
                        >
                          {searchingCep ? "Buscando..." : "Buscar"}
                        </Button>
                      </motion.div>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Preencha o CEP para buscar automaticamente os dados do
                      endereço
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Address Form */}
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <HomeIcon fontSize="small" color="primary" />
                    Dados do Endereço
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        id="address-street"
                        name="address-street"
                        label="Rua"
                        placeholder="Ex: Avenida Paulista"
                        fullWidth
                        required
                        value={street}
                        error={!isStreetValid && submitted}
                        helperText={
                          !isStreetValid && submitted
                            ? STRING_NOT_NULL_HELPER
                            : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          setIsStreetValid(value.length > 0);
                          setStreet(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <LocationOnIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        id="address-number"
                        name="address-number"
                        label="Número"
                        placeholder="Ex: 1500"
                        fullWidth
                        required
                        value={houseNumber}
                        error={!isHouseNumberValid && submitted}
                        helperText={
                          !isHouseNumberValid && submitted
                            ? STRING_NOT_NULL_HELPER
                            : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          setIsHouseNumberValid(value.length > 0);
                          setHouseNumber(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <NumbersIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        id="address-neighborhood"
                        name="address-neighborhood"
                        label="Bairro"
                        placeholder="Ex: Centro"
                        fullWidth
                        required
                        error={!isNeighborhoodValid && submitted}
                        value={neighborhood}
                        helperText={
                          !isNeighborhoodValid && submitted
                            ? STRING_NOT_NULL_HELPER
                            : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          setIsNeighborhoodValid(value.length > 0);
                          setNeighborhood(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <ApartmentIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        id="address-city"
                        name="address-city"
                        label="Cidade"
                        placeholder="Ex: São Paulo"
                        fullWidth
                        required
                        error={!isCityValid && submitted}
                        value={city}
                        helperText={
                          !isCityValid && submitted
                            ? STRING_NOT_NULL_HELPER
                            : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          setIsCityValid(value.length > 0);
                          setCity(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <BusinessIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        id="address-uf"
                        name="address-uf"
                        label="Estado"
                        placeholder="Ex: SP"
                        fullWidth
                        required
                        error={!isUfValid && submitted}
                        value={uf}
                        helperText={
                          !isUfValid && submitted ? STRING_NOT_NULL_HELPER : ""
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          setIsUfValid(value.length > 0);
                          setUf(value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <PublicIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        id="address-complement"
                        name="address-complement"
                        label="Complemento"
                        placeholder="Ex: Apto 42, Bloco B"
                        fullWidth
                        value={complement}
                        onChange={(event) => {
                          setComplement(event.target.value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <HomeIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        id="address-reference"
                        name="address-reference"
                        label="Referência"
                        placeholder="Ex: Próximo ao Shopping"
                        fullWidth
                        value={reference}
                        onChange={(event) => {
                          setReference(event.target.value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <BookmarkIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Footer with actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  p: 3,
                  pt: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={closeModal}
                    disabled={saving}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Cancelar
                  </Button>

                  <motion.div
                    whileHover={!saving ? "hover" : undefined}
                    whileTap={!saving ? "tap" : undefined}
                    variants={buttonVariants}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={saving || success}
                      startIcon={success ? <CheckCircleIcon /> : <SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        bgcolor: success
                          ? theme.palette.success.main
                          : undefined,
                        position: "relative",
                      }}
                    >
                      {saving
                        ? "Salvando..."
                        : success
                        ? "Salvo!"
                        : isEditing
                        ? "Atualizar Endereço"
                        : "Cadastrar Endereço"}

                      {saving && (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      )}
                    </Button>
                  </motion.div>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
